// src/pages/statistiques/StatsFinances.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Table, Form, Button, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';
import * as XLSX from 'xlsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const formatMontant = (montant) => {
  if (montant === null || montant === undefined || isNaN(montant)) return '0 FCFA';
  return new Intl.NumberFormat('fr-FR').format(montant) + ' FCFA';
};

const formatNombre = (nombre) => {
  if (nombre === null || nombre === undefined || isNaN(nombre)) return '0';
  return new Intl.NumberFormat('fr-FR').format(nombre);
};

// Palette de couleurs pour les graphiques
const CHART_COLORS = {
  primary: COLORS.primary,
  secondary: COLORS.secondary,
  success: COLORS.success,
  danger: COLORS.danger,
  warning: COLORS.warning,
  info: COLORS.info,
  purple: COLORS.purple,
  pink: COLORS.pink,
  indigo: COLORS.indigo,
  gray: COLORS.gray,
  cyan: COLORS.cyan,
  rose: COLORS.rose,
  orange: '#f59e0b',
  teal: '#14b8a6',
  emerald: '#059669',
  amber: '#d97706',
  sky: '#0ea5e9',
  violet: '#7c3aed',
  fuchsia: '#d946ef',
};

const hexToRgba = (hex, alpha = 0.5) => {
  hex = hex.replace('#', '');
  if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const DATASET_COLORS = [
  CHART_COLORS.purple,
  CHART_COLORS.cyan,
  CHART_COLORS.success,
  CHART_COLORS.orange,
  CHART_COLORS.pink,
  CHART_COLORS.primary,
  CHART_COLORS.teal,
  CHART_COLORS.amber,
  CHART_COLORS.rose,
  CHART_COLORS.indigo,
];

export default function StatsFinances() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEglise, setSelectedEglise] = useState('all');
  const [eglises, setEglises] = useState([]);

  const [entreesPage, setEntreesPage] = useState(1);
  const [entreesPerPage] = useState(5);
  const [depensesPage, setDepensesPage] = useState(1);
  const [depensesPerPage] = useState(5);

  const [statsData, setStatsData] = useState({
    resume: {
      totalEntrees: 0,
      totalDepenses: 0,
      solde: 0,
      nbEntrees: 0,
      nbDepenses: 0
    },
    mensuel: [],
    parTypeEntree: [],
    parTypeDepense: [],
    evolution: [],
    comparaison: [],
    eglises: []
  });

  const years = [];
  for (let i = new Date().getFullYear() - 5; i <= new Date().getFullYear(); i++) {
    years.push(i);
  }

  useEffect(() => {
    fetchEglises();
  }, []);

  useEffect(() => {
    fetchStats();
    setEntreesPage(1);
    setDepensesPage(1);
  }, [selectedYear, selectedEglise]);

  const fetchEglises = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&type=siege&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        setEglises(result.eglises || []);
      }
    } catch (err) {
      console.error('Erreur chargement églises:', err);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      let url = `https://josephsbusiness.com/api/stats/finances.php?user_id=${userId}&annee=${selectedYear}`;
      if (selectedEglise !== 'all') {
        url += `&eglise=${encodeURIComponent(selectedEglise)}`;
      }
      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
      const result = await response.json();
      if (result.success) {
        const data = result.data || {};
        setStatsData({
          resume: {
            totalEntrees: data.resume?.totalEntrees || 0,
            totalDepenses: data.resume?.totalDepenses || 0,
            solde: data.resume?.solde || 0,
            nbEntrees: data.resume?.nbEntrees || 0,
            nbDepenses: data.resume?.nbDepenses || 0
          },
          mensuel: Array.isArray(data.mensuel) ? data.mensuel : [],
          parTypeEntree: Array.isArray(data.parTypeEntree) ? data.parTypeEntree : [],
          parTypeDepense: Array.isArray(data.parTypeDepense) ? data.parTypeDepense : [],
          evolution: Array.isArray(data.evolution) ? data.evolution : [],
          comparaison: Array.isArray(data.comparaison) ? data.comparaison : [],
          eglises: Array.isArray(data.eglises) ? data.eglises : []
        });
      } else {
        setError(result.message || 'Erreur de chargement des statistiques');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!statsData.mensuel.length && !statsData.parTypeEntree.length && !statsData.parTypeDepense.length) {
      alert('Aucune donnée à exporter');
      return;
    }
    const wb = XLSX.utils.book_new();
    const resumeData = [
      ['RÉSUMÉ FINANCIER', selectedYear],
      ['Total entrées', formatMontant(statsData.resume.totalEntrees)],
      ['Total dépenses', formatMontant(statsData.resume.totalDepenses)],
      ['Solde', formatMontant(statsData.resume.solde)],
      ['Nombre d\'opérations (entrées)', statsData.resume.nbEntrees],
      ['Nombre d\'opérations (dépenses)', statsData.resume.nbDepenses]
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(resumeData);
    XLSX.utils.book_append_sheet(wb, ws1, "Résumé");
    if (statsData.mensuel.length > 0) {
      const mensuelData = statsData.mensuel.map(item => ({
        'Mois': item.mois || '',
        'Entrées': item.entrees || 0,
        'Dépenses': item.depenses || 0,
        'Solde': (item.entrees || 0) - (item.depenses || 0)
      }));
      const ws2 = XLSX.utils.json_to_sheet(mensuelData);
      XLSX.utils.book_append_sheet(wb, ws2, "Mensuel");
    }
    if (statsData.parTypeEntree.length > 0) {
      const entreesData = statsData.parTypeEntree.map(item => ({
        'Type': item.type || '',
        'Montant': item.montant || 0,
        'Pourcentage': (item.pourcentage || 0) + '%'
      }));
      const ws3 = XLSX.utils.json_to_sheet(entreesData);
      XLSX.utils.book_append_sheet(wb, ws3, "Types d'entrées");
    }
    if (statsData.parTypeDepense.length > 0) {
      const depensesData = statsData.parTypeDepense.map(item => ({
        'Type': item.type || '',
        'Montant': item.montant || 0,
        'Pourcentage': (item.pourcentage || 0) + '%'
      }));
      const ws4 = XLSX.utils.json_to_sheet(depensesData);
      XLSX.utils.book_append_sheet(wb, ws4, "Types de dépenses");
    }
    XLSX.writeFile(wb, `statistiques_finances_${selectedYear}.xlsx`);
  };

  // Pagination
  const indexOfLastEntree = entreesPage * entreesPerPage;
  const indexOfFirstEntree = indexOfLastEntree - entreesPerPage;
  const currentEntrees = statsData.parTypeEntree.slice(indexOfFirstEntree, indexOfLastEntree);
  const totalEntreesPages = Math.ceil(statsData.parTypeEntree.length / entreesPerPage);

  const indexOfLastDepense = depensesPage * depensesPerPage;
  const indexOfFirstDepense = indexOfLastDepense - depensesPerPage;
  const currentDepenses = statsData.parTypeDepense.slice(indexOfFirstDepense, indexOfLastDepense);
  const totalDepensesPages = Math.ceil(statsData.parTypeDepense.length / depensesPerPage);

  const renderPagination = (currentPage, totalPages, setPageFunction) => {
    if (totalPages <= 1) return null;
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    return (
      <Pagination size="sm" className="mt-2 mb-0 justify-content-center modern-pagination">
        <Pagination.First onClick={() => setPageFunction(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => setPageFunction(currentPage - 1)} disabled={currentPage === 1} />
        {startPage > 1 && (
          <>
            <Pagination.Item onClick={() => setPageFunction(1)}>1</Pagination.Item>
            {startPage > 2 && <Pagination.Ellipsis disabled />}
          </>
        )}
        {[...Array(endPage - startPage + 1)].map((_, i) => {
          const page = startPage + i;
          return (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => setPageFunction(page)}
            >
              {page}
            </Pagination.Item>
          );
        })}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
            <Pagination.Item onClick={() => setPageFunction(totalPages)}>{totalPages}</Pagination.Item>
          </>
        )}
        <Pagination.Next onClick={() => setPageFunction(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => setPageFunction(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>
    );
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des statistiques</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const moisFrancais = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const mensuelComplete = moisFrancais.map((mois, index) => {
    const found = statsData.mensuel.find(item =>
      item.mois === mois ||
      item.mois === (index + 1).toString() ||
      (item.mois && item.mois.toLowerCase() === mois.toLowerCase())
    );
    return {
      mois: mois,
      entrees: found ? found.entrees || 0 : 0,
      depenses: found ? found.depenses || 0 : 0
    };
  });

  const hasMensuelData = mensuelComplete.some(m => m.entrees > 0 || m.depenses > 0);
  const hasTypeEntreeData = statsData.parTypeEntree.length > 0;
  const hasTypeDepenseData = statsData.parTypeDepense.length > 0;
  const hasEvolutionData = statsData.evolution.length > 0;
  const hasEglisesData = statsData.eglises.length > 0;

  // Données pour les graphiques
  const mensuelData = {
    labels: mensuelComplete.map(item => item.mois),
    datasets: [
      {
        label: 'Entrées',
        data: mensuelComplete.map(item => item.entrees),
        backgroundColor: hexToRgba(CHART_COLORS.success, 0.5),
        borderColor: CHART_COLORS.success,
        borderWidth: 2,
      },
      {
        label: 'Dépenses',
        data: mensuelComplete.map(item => item.depenses),
        backgroundColor: hexToRgba(CHART_COLORS.danger, 0.5),
        borderColor: CHART_COLORS.danger,
        borderWidth: 2,
      },
    ],
  };

  const typeEntreesData = {
    labels: statsData.parTypeEntree.map(item => item.type || 'Non spécifié'),
    datasets: [{
      data: statsData.parTypeEntree.map(item => item.montant || 0),
      backgroundColor: DATASET_COLORS.slice(0, statsData.parTypeEntree.length),
      borderColor: 'white',
      borderWidth: 2,
    }],
  };

  const typeDepensesData = {
    labels: statsData.parTypeDepense.map(item => item.type || 'Non spécifié'),
    datasets: [{
      data: statsData.parTypeDepense.map(item => item.montant || 0),
      backgroundColor: DATASET_COLORS.slice(0, statsData.parTypeDepense.length),
      borderColor: 'white',
      borderWidth: 2,
    }],
  };

  const evolutionData = {
    labels: statsData.evolution.map(item => item.annee?.toString() || ''),
    datasets: [
      {
        label: 'Entrées',
        data: statsData.evolution.map(item => item.entrees || 0),
        borderColor: CHART_COLORS.success,
        backgroundColor: hexToRgba(CHART_COLORS.success, 0.2),
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Dépenses',
        data: statsData.evolution.map(item => item.depenses || 0),
        borderColor: CHART_COLORS.danger,
        backgroundColor: hexToRgba(CHART_COLORS.danger, 0.2),
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const comparaisonData = {
    labels: ['Entrées', 'Dépenses'],
    datasets: [{
      data: [statsData.resume.totalEntrees, statsData.resume.totalDepenses],
      backgroundColor: [CHART_COLORS.success, CHART_COLORS.danger],
      borderColor: 'white',
      borderWidth: 2,
    }],
  };

  const eglisesData = {
    labels: statsData.eglises.map(item => item.eglise || 'Non spécifié'),
    datasets: [
      {
        label: 'Entrées',
        data: statsData.eglises.map(item => item.entrees || 0),
        backgroundColor: hexToRgba(CHART_COLORS.success, 0.5),
        borderColor: CHART_COLORS.success,
        borderWidth: 2,
      },
      {
        label: 'Dépenses',
        data: statsData.eglises.map(item => item.depenses || 0),
        backgroundColor: hexToRgba(CHART_COLORS.danger, 0.5),
        borderColor: CHART_COLORS.danger,
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 10 },
          padding: 15,
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.raw;
            if (value === null || value === undefined || isNaN(value)) return `${label}: 0`;
            if (context.dataset.label?.includes('Entrées') || context.dataset.label?.includes('Dépenses')) {
              return `${label}: ${formatMontant(value)}`;
            }
            return `${label}: ${formatNombre(value)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
            else if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
            return value;
          }
        }
      }
    }
  };

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.success}, ${COLORS.primary})` }}>
                <i className="ri-money-euro-circle-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Statistiques financières</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern" style={{ background: `${CHART_COLORS.success}20`, color: CHART_COLORS.success }}>
                    <i className="ri-arrow-up-line"></i>  {formatMontant(statsData.resume.totalEntrees)}
                  </span>
                  <span className="badge-modern" style={{ background: `${CHART_COLORS.danger}20`, color: CHART_COLORS.danger }}>
                    <i className="ri-arrow-down-line"></i>  {formatMontant(statsData.resume.totalDepenses)}
                  </span>
                  {/* <span className="badge-modern" style={{ background: `${statsData.resume.solde >= 0 ? CHART_COLORS.success : CHART_COLORS.danger}20`, color: statsData.resume.solde >= 0 ? CHART_COLORS.success : CHART_COLORS.danger }}>
                    <i className="ri-scales-2-line"></i> Solde {formatMontant(statsData.resume.solde)}
                  </span> */}
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Form.Select
                size="sm"
                value={selectedEglise}
                onChange={(e) => setSelectedEglise(e.target.value)}
                style={{ width: '180px', border: '1px solid #d1d5db', background: 'white' }}
              >
                <option value="all">Toutes les églises</option>
                {eglises.map(eglise => (
                  <option key={eglise.matricule} value={eglise.matricule}>
                    {eglise.nom}
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ width: '100px', border: '1px solid #d1d5db', background: 'white' }}
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Form.Select>
              <button className="btn-modern-outline" onClick={exportToExcel}>
                <i className="ri-file-excel-line"></i> Excel
              </button>
              <button className="btn-modern-outline" onClick={fetchStats} disabled={loading}>
                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i> Actualiser
              </button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="mb-3" style={{ borderLeft: `4px solid ${COLORS.danger}` }}>
              <div className="d-flex align-items-center">
                <i className="ri-error-warning-line me-2" style={{ color: COLORS.danger, fontSize: '1.2rem' }}></i>
                <span>{error}</span>
              </div>
            </Alert>
          )}

          {/* ===== KPI CARDS ===== */}
          <Row className="g-3 mb-4">
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: CHART_COLORS.success }}>
                <div className="kpi-icon" style={{ background: `${CHART_COLORS.success}20`, color: CHART_COLORS.success }}>
                  <i className="ri-arrow-up-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Total entrées</span>
                  <h3 className="kpi-value" style={{ color: CHART_COLORS.success }}>{formatMontant(statsData.resume.totalEntrees)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: CHART_COLORS.danger }}>
                <div className="kpi-icon" style={{ background: `${CHART_COLORS.danger}20`, color: CHART_COLORS.danger }}>
                  <i className="ri-arrow-down-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Total dépenses</span>
                  <h3 className="kpi-value" style={{ color: CHART_COLORS.danger }}>{formatMontant(statsData.resume.totalDepenses)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: statsData.resume.solde >= 0 ? CHART_COLORS.success : CHART_COLORS.danger }}>
                <div className="kpi-icon" style={{ background: `${statsData.resume.solde >= 0 ? CHART_COLORS.success : CHART_COLORS.danger}20`, color: statsData.resume.solde >= 0 ? CHART_COLORS.success : CHART_COLORS.danger }}>
                  <i className="ri-scales-2-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Solde</span>
                  <h3 className="kpi-value" style={{ color: statsData.resume.solde >= 0 ? CHART_COLORS.success : CHART_COLORS.danger }}>{formatMontant(statsData.resume.solde)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: CHART_COLORS.primary }}>
                <div className="kpi-icon" style={{ background: `${CHART_COLORS.primary}20`, color: CHART_COLORS.primary }}>
                  <i className="ri-swap-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Opérations</span>
                  <h3 className="kpi-value" style={{ color: CHART_COLORS.primary }}>{formatNombre(statsData.resume.nbEntrees + statsData.resume.nbDepenses)}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== GRAPHIQUES ===== */}
          <Row className="g-3 mb-4">
            <Col md={8}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-bar-chart-line me-2" style={{ color: CHART_COLORS.primary }}></i>
                    Évolution mensuelle ({selectedYear})
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '300px', padding: '1rem' }}>
                  {hasMensuelData ? <Bar data={mensuelData} options={chartOptions} /> : <div className="text-center text-muted small py-5">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-pie-chart-line me-2" style={{ color: CHART_COLORS.primary }}></i>
                    Comparaison
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '300px', padding: '1rem' }}>
                  {statsData.resume.totalEntrees > 0 || statsData.resume.totalDepenses > 0 ? <Pie data={comparaisonData} options={chartOptions} /> : <div className="text-center text-muted small py-5">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.purple, fontSize: '0.95rem' }}>
                    <i className="ri-pie-chart-line me-2" style={{ color: CHART_COLORS.purple }}></i>
                    Répartition des entrées
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {hasTypeEntreeData ? <Pie data={typeEntreesData} options={chartOptions} /> : <div className="text-center text-muted small py-5">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.danger, fontSize: '0.95rem' }}>
                    <i className="ri-pie-chart-line me-2" style={{ color: CHART_COLORS.danger }}></i>
                    Répartition des dépenses
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {hasTypeDepenseData ? <Pie data={typeDepensesData} options={chartOptions} /> : <div className="text-center text-muted small py-5">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={12}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.info, fontSize: '0.95rem' }}>
                    <i className="ri-line-chart-line me-2" style={{ color: CHART_COLORS.info }}></i>
                    Évolution annuelle
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '300px', padding: '1rem' }}>
                  {hasEvolutionData ? <Line data={evolutionData} options={chartOptions} /> : <div className="text-center text-muted small py-5">Aucune donnée d'évolution disponible</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {selectedEglise === 'all' && (
            <Row className="g-3 mb-4">
              <Col md={12}>
                <Card className="chart-card-modern">
                  <Card.Header className="chart-header">
                    <h6 className="fw-semibold mb-0" style={{ color: COLORS.secondary, fontSize: '0.95rem' }}>
                      <i className="ri-building-line me-2" style={{ color: CHART_COLORS.orange }}></i>
                      Répartition par église ({selectedYear})
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ height: '300px', padding: '1rem' }}>
                    {hasEglisesData ? <Bar data={eglisesData} options={{ ...chartOptions, indexAxis: 'y' }} /> : <div className="text-center text-muted small py-5">Aucune donnée disponible</div>}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* ===== TABLEAUX ===== */}
          <Row className="mt-4">
            <Col md={6}>
              <Card className="table-card-modern">
                <Card.Header className="table-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-semibold mb-0" style={{ color: COLORS.purple, fontSize: '0.95rem' }}>
                      <i className="ri-price-tag-3-line me-2" style={{ color: CHART_COLORS.purple }}></i>
                      Types d'entrées ({selectedYear})
                    </h6>
                    {hasTypeEntreeData && <span className="badge-count">{statsData.parTypeEntree.length} type{statsData.parTypeEntree.length > 1 ? 's' : ''}</span>}
                  </div>
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column">
                  {hasTypeEntreeData ? (
                    <>
                      <div style={{ flex: 1, overflowX: 'auto' }}>
                        <Table hover size="sm" className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="px-3 py-2">Type</th>
                              <th className="px-3 py-2 text-end">Montant</th>
                              <th className="px-3 py-2 text-end">Pourcentage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentEntrees.map((item, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2">{item.type || 'Non spécifié'}</td>
                                <td className="px-3 py-2 text-end fw-semibold" style={{ color: CHART_COLORS.success }}>{formatMontant(item.montant || 0)}</td>
                                <td className="px-3 py-2 text-end">
                                  <span style={{ color: CHART_COLORS.purple }}>{(item.pourcentage || 0).toFixed(1)}%</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {renderPagination(entreesPage, totalEntreesPages, setEntreesPage)}
                    </>
                  ) : <div className="text-center py-4 text-muted small">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="table-card-modern">
                <Card.Header className="table-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-semibold mb-0" style={{ color: COLORS.danger, fontSize: '0.95rem' }}>
                      <i className="ri-price-tag-3-line me-2" style={{ color: CHART_COLORS.danger }}></i>
                      Types de dépenses ({selectedYear})
                    </h6>
                    {hasTypeDepenseData && <span className="badge-count">{statsData.parTypeDepense.length} type{statsData.parTypeDepense.length > 1 ? 's' : ''}</span>}
                  </div>
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column">
                  {hasTypeDepenseData ? (
                    <>
                      <div style={{ flex: 1, overflowX: 'auto' }}>
                        <Table hover size="sm" className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="px-3 py-2">Type</th>
                              <th className="px-3 py-2 text-end">Montant</th>
                              <th className="px-3 py-2 text-end">Pourcentage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentDepenses.map((item, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2">{item.type || 'Non spécifié'}</td>
                                <td className="px-3 py-2 text-end fw-semibold" style={{ color: CHART_COLORS.danger }}>{formatMontant(item.montant || 0)}</td>
                                <td className="px-3 py-2 text-end">
                                  <span style={{ color: CHART_COLORS.primary }}>{(item.pourcentage || 0).toFixed(1)}%</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {renderPagination(depensesPage, totalDepensesPages, setDepensesPage)}
                    </>
                  ) : <div className="text-center py-4 text-muted small">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>

      {/* ===== STYLES ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }

        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-content { flex: 1; }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .fs-24 { font-size: 1.5rem; }

        .chart-card-modern {
          background: white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
          border: none;
          border-radius: 0px;
        }
        .chart-card-modern:hover {
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .chart-header {
          background: transparent;
          border-bottom: 1px solid #e9edf2;
          padding: 0.75rem 1.25rem;
        }

        .table-card-modern {
          background: white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
          border: none;
          border-radius: 0px;
        }
        .table-card-modern:hover {
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .table-card-modern .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .table-header {
          background: transparent;
          border-bottom: 1px solid #e9edf2;
          padding: 0.75rem 1.25rem;
        }
        .table-light {
          background: #f8fafc;
        }
        .badge-count {
          background: rgba(0,0,0,0.05);
          color: #475569;
          padding: 0.2rem 0.6rem;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 500;
        }

        .modern-pagination .page-link {
          border: none;
          margin: 0 2px;
          border-radius: 4px;
          color: #64748b;
          font-size: 0.75rem;
          padding: 0.3rem 0.6rem;
        }
        .modern-pagination .page-link:hover {
          background: ${COLORS.primary};
          color: white;
        }
        .modern-pagination .page-item.active .page-link {
          background: ${COLORS.primary};
          color: white;
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </>
  );
}